import React, { useCallback, useContext, useEffect, useRef, useState, useMemo } from 'react';
import styled from 'styled-components';
import { HELP_DESK_SORT_OPTIONS, HELP_DESK_STATUS_OPTIONS } from '@ubeya/shared/constants/helpDesk';
import { CircleIcon, DropDown, Filter, Input, Select } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import { ReactComponent as SortIcon } from '@ubeya/shared-web/assets/BookingBoard/sort.svg';
import { ReactComponent as SearchIcon } from '@ubeya/shared-web/assets/search.svg';
import { ReactComponent as TickIcon } from '@ubeya/shared-web/assets/tick.svg';
import { useHelpDeskAnalytics } from '../../helpDeskAnalytics';
import BaseSortCheck from './SortCheck';

const ToolbarRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  padding: 16px;
  padding-top: 7px;
  border-bottom: ${({ $isSearchOpen, theme }) => ($isSearchOpen ? 'none' : `1px solid ${theme.colors.grey2}`)};
  flex-shrink: 0;
  min-height: 48px;
`;

const StatusDropdownWrap = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: stretch;
  margin-right: 7px;

  & .label-wrapper {
    width: 100%;
  }
`;

const StatusSelect = styled(Select)`
  > .select {
    height: 34px;
  }

  .select-label {
    line-height: 20px;
    padding: 7px 10px;
  }
`;

const ToolbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
`;

const EmptyIcon = styled.div`
  width: 20px;
  height: 20px;
  margin-right: 8px;
`;

const SortCheck = styled(BaseSortCheck)`
  svg {
    color: ${({ theme }) => theme.colors.grey4};
    fill: ${({ theme }) => theme.colors.grey4};
  }

  path {
    fill: ${({ theme }) => theme.colors.grey4};
  }
`;

const SearchTrigger = styled(CircleIcon)`
  svg {
    width: 20px;
    height: 20px;
  }
`;

const SearchWrap = styled.div`
  padding: 16px;
  padding-top: 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.grey2};
  flex-shrink: 0;
`;

const ToolbarSearch = styled(Input)`
  padding: 7px 8px;
  border-radius: 4px;
  height: 34px;
  min-height: 34px;
`;

const getFilterItems = (filters, key) => (Array.isArray(filters?.[key]) ? filters[key] : []);

const getFiltersBySlug = (filters) =>
  [...getFilterItems(filters, 'basic'), ...getFilterItems(filters, 'fields')].reduce((all, filter) => {
    const { slug, value } = filter || {};

    return slug ? { ...all, [slug]: JSON.stringify(value ?? null) } : all;
  }, {});

const getChangedFilterTypes = (previousFilters, nextFilters) => {
  const previousBySlug = getFiltersBySlug(previousFilters);
  const nextBySlug = getFiltersBySlug(nextFilters);
  const filterTypes = new Set([...Object.keys(previousBySlug), ...Object.keys(nextBySlug)]);

  return [...filterTypes].filter((filterType) => previousBySlug[filterType] !== nextBySlug[filterType]);
};

const HelpDeskTicketsToolbar = ({
  status,
  onStatusChange,
  total,
  sort,
  onSortChange,
  filters,
  setFilters,
  disabledFilters,
  search,
  onSearchChange
}) => {
  const { t } = useContext(ConfigContext);
  const statusSelectOptions = useMemo(
    () => HELP_DESK_STATUS_OPTIONS.map(({ value, labelKey }) => ({ value, label: t(labelKey) })),
    [t]
  );

  const formatStatusValue = useCallback(
    ({ displayValue }) => (typeof total === 'number' ? `${displayValue} (${total})` : displayValue),
    [total]
  );
  const { trackHelpDeskEvent } = useHelpDeskAnalytics();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchInputRef = useRef(null);

  const handleSortChange = useCallback(
    (value) => {
      if (value === sort) return;

      onSortChange(value);
      trackHelpDeskEvent({
        eventName: 'Help Desk - Admin Used Sort',
        context: 'Help Desk ticket toolbar',
        eventProperties: {
          sortType: value
        }
      });
    },
    [onSortChange, sort, trackHelpDeskEvent]
  );

  const handleFiltersChange = useCallback(
    (nextFilters) => {
      const changedFilterTypes = getChangedFilterTypes(filters, nextFilters);

      setFilters(nextFilters);
      changedFilterTypes.forEach((filterType) => {
        trackHelpDeskEvent({
          eventName: 'Help Desk - Admin Used Filter',
          context: 'Help Desk ticket toolbar',
          eventProperties: {
            filterType
          }
        });
      });
    },
    [filters, setFilters, trackHelpDeskEvent]
  );

  const handleSearchToggle = useCallback(() => {
    setIsSearchOpen((prev) => !prev);
  }, []);

  useEffect(() => {
    if (isSearchOpen) {
      searchInputRef.current?.focus();
    }
  }, [isSearchOpen]);

  return (
    <>
      <ToolbarRow $isSearchOpen={isSearchOpen}>
        <StatusDropdownWrap>
          <StatusSelect
            options={statusSelectOptions}
            value={status}
            onChange={onStatusChange}
            searchable={false}
            usePortal
            hideTitle
            formatValue={formatStatusValue}
          />
        </StatusDropdownWrap>

        <ToolbarRight>
          <DropDown
            showArrow={false}
            usePortal
            openFromRight={false}
            menuMinWidth={180}
            renderValue={({ isOpen }) => <CircleIcon icon={SortIcon} isActive={isOpen} tooltip={t('sort')} />}>
            {HELP_DESK_SORT_OPTIONS.map(({ value, labelKey }) => (
              <DropDown.Item key={value} onClick={() => handleSortChange(value)}>
                {sort === value ? (
                  <SortCheck>
                    <TickIcon />
                  </SortCheck>
                ) : (
                  <EmptyIcon />
                )}
                {t(labelKey)}
              </DropDown.Item>
            ))}
          </DropDown>

          <Filter
            selectedKey="help-desk-tickets"
            filters={filters}
            setFilters={handleFiltersChange}
            disabledFilters={disabledFilters}
            activeOnOpen
            usePortal
          />

          <SearchTrigger icon={SearchIcon} isActive={isSearchOpen} onClick={handleSearchToggle} tooltip={t('search')} />
        </ToolbarRight>
      </ToolbarRow>

      {isSearchOpen && (
        <SearchWrap>
          <ToolbarSearch inputRef={searchInputRef} value={search} onChange={onSearchChange} placeholder={t('search')} />
        </SearchWrap>
      )}
    </>
  );
};

export default HelpDeskTicketsToolbar;
